import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from 'react-i18next';
import { getSemiMonthlyAnchors } from '../../../helpers/invoicePlans';
import { convertToDollars } from '../../../helpers/money';
import { capitalizeFirstLetter, currencyFormatter } from '../../../helpers/utils';
import { DAYS_OF_THE_WEEK } from '../../consts';
import { BillingIntervalEnum } from '../../enums';
import { SummaryViewTitle } from '../widgets';

dayjs.extend(utc);

const NewEnrollmentInvoicesView = ({ tuition, fees }) => {
  const { t } = useTranslation();

  const getInvoiceDueDate = (): string => {
    if (tuition?.dueDate) return '';
    const date = dayjs(tuition.dueDate);

    // Normalize casing: depending on the entry path the stored billingInterval can be
    // title-cased ('Twice Per Month' / 'Twice per month'), while BillingIntervalEnum is
    // lowercase. Lowercase before matching so every casing maps to the same case.
    switch (tuition.billingInterval?.toLowerCase()) {
      case BillingIntervalEnum.WEEKLY:
        return t('enrollments.billingInterval.weekly', {
          day: t(DAYS_OF_THE_WEEK[date.day()]),
        });
      case BillingIntervalEnum.BIWEEKLY:
        return t('enrollments.billingInterval.biweekly', {
          day: t(DAYS_OF_THE_WEEK[date.day()]),
        });
      case BillingIntervalEnum.MONTHLY:
        return t('enrollments.billingInterval.monthly', {
          day: date.get('date'),
        });
      case BillingIntervalEnum.TWICE_PER_MONTH: {
        // Match the engine's clamped semi-monthly anchors (D and min(D + 15, lastDay))
        // rather than a fixed +15, which could claim a day that is never charged.
        const anchorDay = date.get('date');
        const { firstAnchor, secondAnchor } = getSemiMonthlyAnchors(date.year(), date.month(), anchorDay);
        return t('enrollments.billingInterval.twiceAMonth', {
          firstDate: firstAnchor,
          secondDate: secondAnchor,
        });
      }
      default:
        return '';
    }
  };

  const getFormatedDate = (date) => {
    if (!date) return '';
    return dayjs(date).utc().format('MM/DD/YYYY');
  };
  if (!tuition && !fees) return null;

  return (
    <div className="schedule-form">
      <SummaryViewTitle title={t('enrollments.invoicesPageTitle')} showEditIcon={false} />
      {tuition && Object.keys(tuition).length > 0 && (
        <>
          <h3 className="pb-4 text-xl">{t('enrollments.tuitionRecurring')}</h3>
          <div className="grid grid-cols-3 gap-3 py-4">
            <div className="flex flex-col gap-2">
              <h3>{t('enrollments.recurringTotal')}</h3>
              <div>{currencyFormatter(convertToDollars(tuition.amount))}</div>
            </div>
            <div className="flex flex-col gap-2">
              <h3>{t('enrollments.billingsShedule')}</h3>
              <div>{capitalizeFirstLetter(tuition.billingInterval)}</div>
            </div>
            <div className="flex flex-col gap-2">
              <h3>{t('enrollments.invoiceDueDate')}</h3>
              <div>{getInvoiceDueDate()}</div>
            </div>
            {tuition.proratedAmount > 0 && (
              <>
                <div className="flex flex-col gap-2">
                  <h3>{t('enrollments.firstMonthProrated')}</h3>
                  <div>{currencyFormatter(tuition.proratedAmount)}</div>
                </div>
                <div className="flex flex-col gap-2">
                  <h3>{t('enrollments.firstInvoiceDue')}</h3>
                  <div>{getFormatedDate(tuition.proratedDueDate)}</div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      {fees && fees.length > 0 && (
        <>
          <h3 className="py-4 text-xl">{t('enrollments.enrollmentOneTime')}</h3>
          {fees.map((fee, index) => (
            <div key={index} className="grid grid-cols-3 gap-3 py-4">
              <div className="flex flex-col gap-2">
                <h3>{t('enrollments.totalAmount')}</h3>
                <div>{currencyFormatter(convertToDollars(fee.amount))}</div>
              </div>
              <div className="flex flex-col gap-2">
                <h3>{t('enrollments.feeDescription')}</h3>
                <div>{fee.name}</div>
              </div>
              <div className="flex flex-col gap-2">
                <h3>{t('enrollments.dueDate')}</h3>
                <div>{getFormatedDate(fee.dueDate)}</div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default NewEnrollmentInvoicesView;
